// The Phase 1 prescribing checks (docs/plans/phase1-consultation.md §4.1, §5.3; CLAUDE.md).
//
// One pure function, `evaluatePrescription`, used by the preview, by the server at signing
// and by the pharmacy at dispensing. It checks:
//
//   - allergy conflicts (allergy.ts);
//   - duplicate therapy: the same substance, or the same therapeutic group, in another line
//     of this prescription, another active line of this visit, or an active course;
//   - maximum single and daily dose, per substance or per product;
//   - weight-based doses (per kg, and weight bands) where the formulary has a rule.
//
// It does NOT check drug interactions, pregnancy, breastfeeding, renal or hepatic dosing:
// no knowledge source exists for them. Nothing here may suggest otherwise.
//
// Every dose limit comes from the tenant's dose rules (rules.ts); none is in code. When a
// rule exists but can't be applied, the line says so (`rx.dose.cannot_check`) instead of
// passing. With no rule, the line says "No dose limit set" (`rx.dose.no_rule`).
//
// Clinical choices, provisional until the clinical safety lead signs them off
// (plan §14, approved as suggested defaults on 25 Sep 2026):
// TODO(clinical-verify) C7: duplicates by substance or therapeutic group; this visit plus
//   courses whose calculated end is in the future; interruptive.
// TODO(clinical-verify) C8: exceeding a maximum interrupts unless the rule says blocking.
// TODO(clinical-verify) C9: only a weight recorded in this visit counts.
// TODO(clinical-verify) C11: a rule that can't be applied interrupts.
// TODO(clinical-verify) C12: below a per-kg rule's low bound is passive.
// TODO(clinical-verify) C13: an estimated age is used and the message marked "(estimated age)".
// TODO(clinical-verify): daily totals sum a substance across every route; a weight band
//   bounds each single dose; a weight outside every band cannot be checked.

import { BUILT_IN_RULE_VERSION, sortAlerts, type PrescribingAlert } from './alerts.ts';
import {
  checkAllergyConflicts,
  type AllergyContext,
  type CodeRef,
  type GroupMembershipRef,
} from './allergy.ts';
import {
  add,
  calculateQuantity,
  compare,
  convertAmount,
  courseDays,
  daysCovered,
  divide,
  doseInProductUnit,
  dosesPerDay,
  formatRatio,
  ingredientAmountPerDose,
  multiply,
  parseDecimal,
  ratioFromScaled,
  type Amount,
  type Duration,
  type FrequencyFacts,
  type QuantityInput,
  type Ratio,
  type Strength,
} from './quantities.ts';
import {
  DOSE_RULE_AMOUNT_DECIMALS,
  isAgeBanded,
  selectDoseRules,
  selectWeightBand,
  WEIGHT_DECIMALS,
  type DoseRule,
  type DoseRuleKind,
  type DoseRuleSubjectType,
} from './rules.ts';

// --- Input ------------------------------------------------------------------------------------

export interface DecimalAmount {
  /** A decimal string ("250", "2.5"), never a JSON number. */
  readonly value: string;
  readonly unit: string;
}

export interface IngredientFacts extends CodeRef {
  readonly strengthNumerator: DecimalAmount;
  readonly strengthDenominator: DecimalAmount;
}

export const STOCK_STATES = ['stocked', 'out_of_stock', 'not_stocked'] as const;
export type StockState = (typeof STOCK_STATES)[number];

/** What the formulary says about one product. */
export interface FormularyFacts {
  readonly formularyItemId: string;
  readonly version: number;
  readonly serviceItemId: string | null;
  readonly display: string;
  readonly doseUnit: string;
  readonly ingredients: readonly IngredientFacts[];
  /** Of the product and of its ingredients. */
  readonly allergenGroups: readonly GroupMembershipRef[];
  readonly therapeuticGroups: readonly GroupMembershipRef[];
  readonly baseUnitContent: DecimalAmount | null;
  readonly quantityCalculable: boolean;
  readonly stock: StockState;
}

/** A line being prescribed, not yet signed. */
export interface DraftLine {
  readonly lineKey: string;
  readonly formularyItemId: string;
  readonly dose: DecimalAmount;
  readonly routeCode: string;
  readonly frequency: FrequencyFacts;
  readonly maxDosesPerDay: number | null;
  readonly duration: Duration | null;
  /** What the prescriber set to dispense, whole base units; null to use the calculation. */
  readonly quantity: number | null;
}

/**
 * A medicine the patient already has: an active line of this visit, or a course from an
 * earlier prescription whose calculated end is still ahead (the API decides which count).
 */
export interface ExistingMedicine {
  readonly lineId: string;
  readonly scope: 'visit' | 'course';
  readonly formularyItemId: string;
  readonly dose: DecimalAmount;
  readonly routeCode: string;
  readonly frequency: FrequencyFacts;
  readonly maxDosesPerDay: number | null;
}

export interface PatientFacts {
  /** Age in whole days at the clinical time; null when no date of birth is recorded. */
  readonly ageDays: number | null;
  readonly ageEstimated: boolean;
  /** This visit's latest final weight (C9), kg × 100. */
  readonly weight: { readonly scaled: number; readonly observationId: string } | null;
}

export interface EvaluatePrescriptionInput {
  readonly lines: readonly DraftLine[];
  readonly existing: readonly ExistingMedicine[];
  /** Facts for every formulary item named by `lines` and `existing`, by formulary item ID. */
  readonly items: ReadonlyMap<string, FormularyFacts>;
  readonly doseRules: readonly DoseRule[];
  readonly allergy: AllergyContext;
  readonly patient: PatientFacts;
}

export interface LineEvaluation {
  readonly lineKey: string;
  readonly alerts: readonly PrescribingAlert[];
  /** Whole base units for the course, or null with the reason it can't be calculated. */
  readonly quantityCalculated: number | null;
  readonly quantityNotCalculatedReason: string | null;
}

export interface PrescriptionEvaluation {
  /** Alerts about the whole prescription (allergies not recorded, uncoded allergies). */
  readonly prescriptionAlerts: readonly PrescribingAlert[];
  readonly lines: readonly LineEvaluation[];
  /** Every alert, in the stable order (tier, rule, line). */
  readonly all: readonly PrescribingAlert[];
}

// --- Helpers ----------------------------------------------------------------------------------

type CannotCheckReason =
  | 'formulary_item_missing'
  | 'dose_invalid'
  | 'strength_invalid'
  | 'units_not_convertible'
  | 'ambiguous_ingredient'
  | 'as_needed_without_maximum'
  | 'frequency_invalid'
  | 'rules_overlap'
  | 'age_unknown'
  | 'weight_outside_bands'
  | 'other_line_not_computable';

class CannotCheck extends Error {
  readonly reason: CannotCheckReason;
  constructor(reason: CannotCheckReason) {
    super(reason);
    this.reason = reason;
  }
}

function decimal(amount: DecimalAmount, reason: CannotCheckReason): Amount {
  const parsed = parseDecimal(amount.value);
  if (!parsed.ok) throw new CannotCheck(reason);
  return { value: parsed.value, unit: amount.unit };
}

function strengthsOf(item: FormularyFacts): Strength[] {
  return item.ingredients.map((i) => ({
    numerator: decimal(i.strengthNumerator, 'strength_invalid'),
    denominator: decimal(i.strengthDenominator, 'strength_invalid'),
  }));
}

const sameCode = (a: { code: string; system: string }, b: { code: string; system: string }) =>
  a.code === b.code && a.system === b.system;

/** A line of the prescription or an existing medicine: what the arithmetic needs. */
interface Contributor {
  readonly ref: string;
  readonly kind: 'draft' | 'visit' | 'course';
  readonly item: FormularyFacts;
  readonly dose: DecimalAmount;
  readonly frequency: FrequencyFacts;
  readonly maxDosesPerDay: number | null;
}

interface Subject {
  readonly type: DoseRuleSubjectType;
  readonly id: string;
  /** For substances; null for the product. */
  readonly ingredient: IngredientFacts | null;
}

function subjectsOf(item: FormularyFacts): Subject[] {
  return [
    ...item.ingredients.map((i): Subject => ({ type: 'substance', id: i.code, ingredient: i })),
    { type: 'formulary_item', id: item.formularyItemId, ingredient: null },
  ];
}

function containsSubject(item: FormularyFacts, subject: Subject): boolean {
  if (subject.type === 'formulary_item') return item.formularyItemId === subject.id;
  const ingredient = subject.ingredient;
  return ingredient !== null && item.ingredients.some((i) => sameCode(i, ingredient));
}

function amountError(code: string): CannotCheckReason {
  if (code === 'ambiguous_ingredient') return 'ambiguous_ingredient';
  if (code === 'strength_invalid') return 'strength_invalid';
  return 'units_not_convertible';
}

/** The amount of a subject in one dose of a contributor, in the rule's unit. */
function amountPerDose(contributor: Contributor, subject: Subject, unit: string): Ratio {
  const item = contributor.item;
  const dose = decimal(contributor.dose, 'dose_invalid');
  const strengths = strengthsOf(item);
  if (subject.type === 'substance') {
    const index = item.ingredients.findIndex(
      (i) => subject.ingredient !== null && sameCode(i, subject.ingredient),
    );
    const strength = strengths[index];
    if (strength === undefined) throw new CannotCheck('units_not_convertible');
    const amount = ingredientAmountPerDose(dose, strength, strengths.length);
    if (!amount.ok) throw new CannotCheck(amountError(amount.code));
    const converted = convertAmount(amount.value.value, amount.value.unit, unit);
    if (!converted.ok) throw new CannotCheck('units_not_convertible');
    return converted.value;
  }
  // A product rule: in the dose's own unit, the product's dose unit, or its one ingredient's measure.
  const direct = convertAmount(dose.value, dose.unit, unit);
  if (direct.ok) return direct.value;
  const inProduct = doseInProductUnit(dose, item.doseUnit, strengths);
  if (inProduct.ok) {
    const converted = convertAmount(inProduct.value.value, inProduct.value.unit, unit);
    if (converted.ok) return converted.value;
  }
  const [only, ...others] = strengths;
  if (only !== undefined && others.length === 0) {
    const amount = ingredientAmountPerDose(dose, only, 1);
    if (amount.ok) {
      const converted = convertAmount(amount.value.value, amount.value.unit, unit);
      if (converted.ok) return converted.value;
    }
  }
  throw new CannotCheck('units_not_convertible');
}

function perDay(contributor: Contributor): Ratio {
  const result = dosesPerDay(contributor.frequency, contributor.maxDosesPerDay);
  if (!result.ok) {
    throw new CannotCheck(
      result.code === 'as_needed_without_maximum'
        ? 'as_needed_without_maximum'
        : 'frequency_invalid',
    );
  }
  return result.value;
}

/** The daily total of a subject across the line and every other contributor containing it. */
function dailyTotal(
  line: Contributor,
  others: readonly Contributor[],
  subject: Subject,
  unit: string,
): Ratio {
  let total = multiply(amountPerDose(line, subject, unit), perDay(line));
  for (const other of others) {
    if (!containsSubject(other.item, subject)) continue;
    try {
      total = add(total, multiply(amountPerDose(other, subject, unit), perDay(other)));
    } catch (error) {
      if (error instanceof CannotCheck) throw new CannotCheck('other_line_not_computable');
      throw error;
    }
  }
  return total;
}

function ruleAmount(scaled: number): Ratio {
  return ratioFromScaled(scaled, DOSE_RULE_AMOUNT_DECIMALS);
}

function doseRuleRef(rule: DoseRule): PrescribingAlert['doseRule'] {
  return { id: rule.id, version: rule.version, approval: rule.approval, source: rule.source };
}

function ruleAlert(
  ruleId: PrescribingAlert['ruleId'],
  rule: DoseRule,
  lineKey: string,
  evidence: readonly string[],
  facts: PrescribingAlert['facts'],
  tier: PrescribingAlert['tier'] = rule.enforcement,
): PrescribingAlert {
  return {
    ruleId,
    tier,
    lineKey,
    ruleVersion: rule.version,
    doseRule: doseRuleRef(rule),
    provisional: rule.approval === 'provisional',
    evidence,
    facts,
  };
}

function builtIn(
  ruleId: PrescribingAlert['ruleId'],
  tier: PrescribingAlert['tier'],
  lineKey: string | null,
  evidence: readonly string[],
  facts: PrescribingAlert['facts'],
  provisional = false,
): PrescribingAlert {
  return {
    ruleId,
    tier,
    lineKey,
    ruleVersion: BUILT_IN_RULE_VERSION,
    doseRule: null,
    provisional,
    evidence,
    facts,
  };
}

function subjectFacts(subject: Subject, item: FormularyFacts): Record<string, string> {
  return subject.ingredient === null
    ? {
        subjectType: 'formulary_item',
        subjectCode: item.formularyItemId,
        subjectDisplay: item.display,
      }
    : {
        subjectType: 'substance',
        subjectCode: subject.ingredient.code,
        subjectDisplay: subject.ingredient.display,
      };
}

// --- Dose checks ------------------------------------------------------------------------------

const DOSE_KINDS: readonly DoseRuleKind[] = [
  'max_single_dose',
  'max_daily_dose',
  'per_kg_single',
  'per_kg_daily',
  'weight_band_dose',
];

interface DoseOutcome {
  readonly alerts: PrescribingAlert[];
  /** True when at least one rule applied to this line (so "No dose limit set" is false). */
  readonly ruleApplied: boolean;
}

function checkRule(
  rule: DoseRule,
  subject: Subject,
  line: Contributor,
  lineKey: string,
  others: readonly Contributor[],
  patient: PatientFacts,
): PrescribingAlert[] {
  const facts = {
    ...subjectFacts(subject, line.item),
    unit: rule.amountUnit,
    ageEstimated: patient.ageEstimated && isAgeBanded(rule),
  };
  const high = rule.highScaled === null ? null : ruleAmount(rule.highScaled);
  const low = rule.lowScaled === null ? null : ruleAmount(rule.lowScaled);
  switch (rule.kind) {
    case 'max_single_dose': {
      const amount = amountPerDose(line, subject, rule.amountUnit);
      if (high !== null && compare(amount, high) > 0) {
        return [
          ruleAlert('rx.dose.max_single', rule, lineKey, [], {
            ...facts,
            amount: formatRatio(amount),
            limit: formatRatio(high),
          }),
        ];
      }
      return [];
    }
    case 'max_daily_dose': {
      const total = dailyTotal(line, others, subject, rule.amountUnit);
      if (high !== null && compare(total, high) > 0) {
        const contributing = others
          .filter((o) => containsSubject(o.item, subject))
          .map((o) => o.ref);
        return [
          ruleAlert('rx.dose.max_daily', rule, lineKey, contributing, {
            ...facts,
            amount: formatRatio(total),
            limit: formatRatio(high),
          }),
        ];
      }
      return [];
    }
    case 'per_kg_single':
    case 'per_kg_daily': {
      const weight = patient.weight;
      if (weight === null) return [builtIn('rx.dose.no_weight', 'interruptive', lineKey, [], {})];
      const kg = ratioFromScaled(weight.scaled, WEIGHT_DECIMALS);
      if (kg.num === 0n) throw new CannotCheck('units_not_convertible');
      const amount =
        rule.kind === 'per_kg_single'
          ? amountPerDose(line, subject, rule.amountUnit)
          : dailyTotal(line, others, subject, rule.amountUnit);
      const perKg = divide(amount, kg);
      const numbers = {
        ...facts,
        per: rule.kind === 'per_kg_single' ? 'dose' : 'day',
        amount: formatRatio(amount),
        perKg: formatRatio(perKg),
        weightKg: formatRatio(kg, WEIGHT_DECIMALS),
      };
      if (high !== null && compare(perKg, high) > 0) {
        return [
          ruleAlert('rx.dose.per_kg_high', rule, lineKey, [weight.observationId], {
            ...numbers,
            limit: formatRatio(high),
          }),
        ];
      }
      if (low !== null && compare(perKg, low) < 0) {
        return [
          ruleAlert(
            'rx.dose.per_kg_low',
            rule,
            lineKey,
            [weight.observationId],
            { ...numbers, limit: formatRatio(low) },
            'passive',
          ),
        ];
      }
      return [];
    }
    case 'weight_band_dose':
      throw new Error('weight bands are checked together');
  }
}

function checkDoses(
  line: Contributor,
  lineKey: string,
  routeCode: string,
  others: readonly Contributor[],
  rules: readonly DoseRule[],
  patient: PatientFacts,
): DoseOutcome {
  const alerts: PrescribingAlert[] = [];
  let ruleApplied = false;
  let ageBandedApplied = false;
  let noWeight = false;

  for (const subject of subjectsOf(line.item)) {
    for (const kind of DOSE_KINDS) {
      const selection = selectDoseRules(rules, {
        subjectType: subject.type,
        subject: subject.id,
        kind,
        routeCode,
        ageDays: patient.ageDays,
      });
      if (selection.kind === 'none') continue;
      if (selection.kind === 'no_band') {
        // Rules exist for other ages. With a known age that means no limit for this one;
        // without an age we can't tell which band applies.
        if (selection.ageUnknown) {
          ruleApplied = true;
          alerts.push(
            builtIn('rx.dose.cannot_check', 'interruptive', lineKey, [], {
              ...subjectFacts(subject, line.item),
              reason: 'age_unknown',
            }),
          );
        }
        continue;
      }
      ruleApplied = true;
      if (selection.kind === 'overlap') {
        alerts.push(
          builtIn('rx.dose.cannot_check', 'interruptive', lineKey, selection.ruleIds, {
            ...subjectFacts(subject, line.item),
            reason: 'rules_overlap',
          }),
        );
        continue;
      }
      try {
        if (kind === 'weight_band_dose') {
          if (selection.rules.some(isAgeBanded)) ageBandedApplied = true;
          if (patient.weight === null) {
            noWeight = true;
            continue;
          }
          const band = selectWeightBand(selection.rules, patient.weight.scaled);
          if (band.kind === 'overlap') throw new CannotCheck('rules_overlap');
          if (band.kind === 'no_band') throw new CannotCheck('weight_outside_bands');
          const rule = band.rule;
          const amount = amountPerDose(line, subject, rule.amountUnit);
          const low = rule.lowScaled === null ? null : ruleAmount(rule.lowScaled);
          const high = rule.highScaled === null ? null : ruleAmount(rule.highScaled);
          const outside =
            (low !== null && compare(amount, low) < 0) ||
            (high !== null && compare(amount, high) > 0);
          if (outside) {
            alerts.push(
              ruleAlert('rx.dose.weight_band', rule, lineKey, [patient.weight.observationId], {
                ...subjectFacts(subject, line.item),
                unit: rule.amountUnit,
                amount: formatRatio(amount),
                bandLow: low === null ? null : formatRatio(low),
                bandHigh: high === null ? null : formatRatio(high),
                weightFromKg: formatRatio(
                  ratioFromScaled(rule.weightFromScaled ?? 0, WEIGHT_DECIMALS),
                  WEIGHT_DECIMALS,
                ),
                weightToKg:
                  rule.weightToScaled === null
                    ? null
                    : formatRatio(
                        ratioFromScaled(rule.weightToScaled, WEIGHT_DECIMALS),
                        WEIGHT_DECIMALS,
                      ),
                weightKg: formatRatio(
                  ratioFromScaled(patient.weight.scaled, WEIGHT_DECIMALS),
                  WEIGHT_DECIMALS,
                ),
                ageEstimated: patient.ageEstimated && isAgeBanded(rule),
              }),
            );
          }
          continue;
        }
        const [rule] = selection.rules;
        if (rule === undefined) continue;
        if (isAgeBanded(rule)) ageBandedApplied = true;
        const found = checkRule(rule, subject, line, lineKey, others, patient);
        for (const a of found) {
          if (a.ruleId === 'rx.dose.no_weight') noWeight = true;
          else alerts.push(a);
        }
      } catch (error) {
        if (!(error instanceof CannotCheck)) throw error;
        alerts.push(
          builtIn('rx.dose.cannot_check', 'interruptive', lineKey, [], {
            ...subjectFacts(subject, line.item),
            kind,
            reason: error.reason,
          }),
        );
      }
    }
  }
  if (noWeight) alerts.push(builtIn('rx.dose.no_weight', 'interruptive', lineKey, [], {}));
  if (ageBandedApplied && patient.ageEstimated) {
    alerts.push(builtIn('rx.estimated_age', 'passive', lineKey, [], {}));
  }
  return { alerts: dedupeCannotCheck(alerts), ruleApplied };
}

/** One "couldn't check" per subject and reason is enough; more is noise. */
function dedupeCannotCheck(alerts: PrescribingAlert[]): PrescribingAlert[] {
  const seen = new Set<string>();
  return alerts.filter((a) => {
    if (a.ruleId !== 'rx.dose.cannot_check') return true;
    const key = `${String(a.facts['subjectCode'])}|${String(a.facts['reason'])}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// --- Duplicates -------------------------------------------------------------------------------

function checkDuplicates(
  line: Contributor,
  lineKey: string,
  earlier: readonly Contributor[],
): PrescribingAlert[] {
  const alerts: PrescribingAlert[] = [];
  for (const other of earlier) {
    const shared = line.item.ingredients.find((i) =>
      other.item.ingredients.some((o) => sameCode(i, o)),
    );
    if (shared !== undefined) {
      alerts.push(
        builtIn('rx.duplicate.ingredient', 'interruptive', lineKey, [other.ref], {
          substanceCode: shared.code,
          substanceDisplay: shared.display,
          otherKind: other.kind,
          otherRef: other.ref,
          otherDisplay: other.item.display,
        }),
      );
      continue;
    }
    for (const group of line.item.therapeuticGroups) {
      const match = other.item.therapeuticGroups.find((g) => sameCode(g, group));
      if (match === undefined) continue;
      alerts.push(
        builtIn(
          'rx.duplicate.group',
          'interruptive',
          lineKey,
          [other.ref, group.membershipId, match.membershipId],
          {
            groupCode: group.code,
            groupDisplay: group.display,
            otherKind: other.kind,
            otherRef: other.ref,
            otherDisplay: other.item.display,
          },
          group.approval === 'provisional' || match.approval === 'provisional',
        ),
      );
      break;
    }
  }
  return alerts;
}

// --- Quantity ---------------------------------------------------------------------------------

function quantityInput(line: DraftLine, item: FormularyFacts): QuantityInput {
  return {
    dose: decimal(line.dose, 'dose_invalid'),
    frequency: line.frequency,
    maxDosesPerDay: line.maxDosesPerDay,
    duration: line.duration,
    product: {
      doseUnit: item.doseUnit,
      strengths: strengthsOf(item),
      baseUnitContent:
        item.baseUnitContent === null ? null : decimal(item.baseUnitContent, 'strength_invalid'),
      quantityCalculable: item.quantityCalculable,
    },
  };
}

function checkQuantity(
  line: DraftLine,
  item: FormularyFacts,
): { quantity: number | null; reason: string | null; alerts: PrescribingAlert[] } {
  let input: QuantityInput;
  try {
    input = quantityInput(line, item);
  } catch (error) {
    if (error instanceof CannotCheck) return { quantity: null, reason: error.reason, alerts: [] };
    throw error;
  }
  const calculated = calculateQuantity(input);
  if (!calculated.ok) return { quantity: null, reason: calculated.code, alerts: [] };
  const alerts: PrescribingAlert[] = [];
  if (
    line.quantity !== null &&
    line.quantity < calculated.value.quantity &&
    line.duration !== null
  ) {
    alerts.push(
      builtIn('rx.quantity_short', 'passive', line.lineKey, [], {
        quantity: line.quantity,
        calculated: calculated.value.quantity,
        daysCovered: daysCovered(line.quantity, input),
        courseDays: courseDays(line.duration),
      }),
    );
  }
  return { quantity: calculated.value.quantity, reason: null, alerts };
}

// --- The evaluation ---------------------------------------------------------------------------

const MISSING_ITEM_ALERTS = (lineKey: string): PrescribingAlert[] => [
  builtIn('rx.dose.cannot_check', 'interruptive', lineKey, [], {
    reason: 'formulary_item_missing',
  }),
];

/**
 * Every alert for a prescription being written. Never throws on missing or odd data: what
 * cannot be checked is reported as such.
 */
export function evaluatePrescription(input: EvaluatePrescriptionInput): PrescriptionEvaluation {
  const existing: Contributor[] = [];
  for (const medicine of input.existing) {
    const item = input.items.get(medicine.formularyItemId);
    if (item === undefined) continue;
    existing.push({
      ref: medicine.lineId,
      kind: medicine.scope,
      item,
      dose: medicine.dose,
      frequency: medicine.frequency,
      maxDosesPerDay: medicine.maxDosesPerDay,
    });
  }
  const drafts: { line: DraftLine; contributor: Contributor | null }[] = input.lines.map((line) => {
    const item = input.items.get(line.formularyItemId);
    return {
      line,
      contributor:
        item === undefined
          ? null
          : {
              ref: line.lineKey,
              kind: 'draft',
              item,
              dose: line.dose,
              frequency: line.frequency,
              maxDosesPerDay: line.maxDosesPerDay,
            },
    };
  });

  const prescriptionLevel = new Map<string, PrescribingAlert>();
  const lines: LineEvaluation[] = [];

  drafts.forEach(({ line, contributor }, index) => {
    const item = contributor?.item ?? null;
    const allergyFindings = checkAllergyConflicts({
      ...input.allergy,
      lineKey: line.lineKey,
      medication:
        item === null
          ? null
          : {
              serviceItemId: item.serviceItemId,
              ingredients: item.ingredients,
              allergenGroups: item.allergenGroups,
            },
    });
    const alerts: PrescribingAlert[] = [];
    for (const finding of allergyFindings) {
      if (finding.lineKey === null) {
        if (!prescriptionLevel.has(finding.ruleId)) prescriptionLevel.set(finding.ruleId, finding);
      } else {
        alerts.push(finding);
      }
    }
    if (contributor === null || item === null) {
      lines.push({
        lineKey: line.lineKey,
        alerts: sortAlerts([...alerts, ...MISSING_ITEM_ALERTS(line.lineKey)]),
        quantityCalculated: null,
        quantityNotCalculatedReason: 'formulary_item_missing',
      });
      return;
    }

    const earlierDrafts = drafts
      .slice(0, index)
      .flatMap((d) => (d.contributor === null ? [] : [d.contributor]));
    const laterDrafts = drafts
      .slice(index + 1)
      .flatMap((d) => (d.contributor === null ? [] : [d.contributor]));
    alerts.push(...checkDuplicates(contributor, line.lineKey, [...earlierDrafts, ...existing]));

    const others = [...earlierDrafts, ...laterDrafts, ...existing];
    const doses = checkDoses(
      contributor,
      line.lineKey,
      line.routeCode,
      others,
      input.doseRules,
      input.patient,
    );
    alerts.push(...doses.alerts);
    if (!doses.ruleApplied)
      alerts.push(builtIn('rx.dose.no_rule', 'passive', line.lineKey, [], {}));

    if (item.stock !== 'stocked') {
      alerts.push(
        builtIn('rx.formulary.not_stocked', 'passive', line.lineKey, [], { state: item.stock }),
      );
    }
    const quantity = checkQuantity(line, item);
    alerts.push(...quantity.alerts);

    lines.push({
      lineKey: line.lineKey,
      alerts: sortAlerts(alerts),
      quantityCalculated: quantity.quantity,
      quantityNotCalculatedReason: quantity.reason,
    });
  });

  const prescriptionAlerts = sortAlerts([...prescriptionLevel.values()]);
  return {
    prescriptionAlerts,
    lines,
    all: sortAlerts([...prescriptionAlerts, ...lines.flatMap((l) => l.alerts)]),
  };
}
